"use client"
import type React from "react"
import { useState } from "react"
import { Modal, Form, Input, Select, Checkbox, Typography, Spin, Button, message } from "antd"
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  BookOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons"
import { useRouter } from "next/navigation"
import axios from "axios"
import toast from "react-hot-toast"
import { BASE_URL } from "@/utils/api"

const { Title, Text } = Typography
const { Option } = Select

interface BrochureDownloadModalProps {
  isOpen: boolean
  onClose: () => void
}

const BrochureDownloadModal: React.FC<BrochureDownloadModalProps> = ({ isOpen, onClose }) => {
  const [form] = Form.useForm()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const router = useRouter()

  // OTP verification states
  const [showOtpField, setShowOtpField] = useState(false)
  const [phoneVerified, setPhoneVerified] = useState(false)
  const [sendingOtp, setSendingOtp] = useState(false)
  const [verifyingOtp, setVerifyingOtp] = useState(false)

  // Function to send OTP
  const sendOTP = async () => {
    try {
      const mobile = form.getFieldValue("phone")
      if (!mobile) {
        message.error("Please enter your phone number")
        return
      }

      setSendingOtp(true)

      // Send OTP API call
const response = await axios.post(`${BASE_URL}send-otp/`, {
  phone_number: `+91${mobile}`,
}, {
  headers: {
    'X-CSRFToken': '{{ csrf_token }}',
    'Content-Type': 'application/json'
  }
})    
  if (response.data.status === "success") {
        message.success(response.data.message)
        setShowOtpField(true)
      } else {
        message.error(response.data.message || "Error sending OTP")
      }
    } catch (error) {
      console.error("Error sending OTP:", error)
      message.error("Error sending OTP. Please try again.")
    } finally {
      setSendingOtp(false)
    }
  }

  // Function to verify OTP
  const verifyOTP = async () => {
    try {
      const mobile =Number( form.getFieldValue("phone"))
      const otp = Number(form.getFieldValue("otp"))

      if (!otp) {
        message.error("Please enter the OTP")
        return
      }

      setVerifyingOtp(true)

      // Verify OTP API call
      const response = await axios.post(
        `${BASE_URL}verify-otp/`, { phone_number: mobile,
          otp: otp},{
          headers: {
            "Content-Type": 'application/json',
          },
        },
      )
      if (response.data.status === "success") {
        message.success(response.data.message)
        setPhoneVerified(true)
      } else {
        message.error(response.data.message || "Invalid OTP")
      }
    } catch (error) {
      console.error("Error verifying OTP:", error)
      message.error("Error verifying OTP. Please try again.")
    } finally {
      setVerifyingOtp(false)
    }
  }
  // Function to handle form submission
  const handleSubmit = async (values: any) => {
    if (!phoneVerified) {
      message.error("Please verify your phone number first")
      return
    }
    setIsSubmitting(true)
    try {
      // Prepare payload
      const payload = {
        full_name: values.fullName,
        email: values.email,
        phone_number: values.phone,
        preferred_course: values.course,
        how_heard_about_us: values.referral || "Not specified",
        terms_and_conditions: values.agreement,
      }
      // Send data to API
      const response = await axios.post(`${BASE_URL}download-brochure/`,  {...payload ,lead_source:"Brochure form"}, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      toast.success(response?.data?.message)
      router.push(`/thank-you-brochure`)
    } catch (error) {
      console.error("Error in form submission:", error)
      toast.error("An error occurred. Please try again later.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    form.resetFields()
    setShowOtpField(false)
    setPhoneVerified(false)
    onClose()
  }

  return (
    <Modal
      title={<h2 className="text-orange text-xl">Download Course Brochure</h2>}
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      width={800}
      bodyStyle={{ maxHeight: "80vh", overflowY: "auto", padding: "20px" }}
    >
      <Spin spinning={isSubmitting} tip="Submitting your request...">
        <Form form={form} layout="vertical" onFinish={handleSubmit} requiredMark="optional">
          {/* Personal Information Section */}
          <div className="mb-3 text-lg font-medium border-b-[1px] border-slate-200 pb-2">
            <div>
              <UserOutlined className="mr-3" />
              <span>Personal Information</span>
            </div>
          </div>
          {phoneVerified && (
          <>
          <Form.Item
            name="fullName"
            label="Full Name"
            rules={[{ required: true, message: "Please provide your full name" }]}
          >
            <Input prefix={<UserOutlined />} placeholder="Please provide your full name" disabled={!phoneVerified} />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: "Please enter your email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="Enter your email to receive the brochure"
              disabled={!phoneVerified}
            />
          </Form.Item>
            </>
          )}
          {/* Phone verification section */}
          <Form.Item
            name="phone"
            label="Phone Number"
            rules={[
              { required: true, message: "Please enter your phone number" },
              { pattern: /^(\+\d{1,3})?\s?\d{10}$/, message: "Please enter a valid phone number" },
            ]}
          >
            <div className="flex items-center gap-2">
              <Input
                prefix={<PhoneOutlined />}
                placeholder="Enter your phone number for faster follow-up"
                disabled={phoneVerified}
                style={{ width: "100%" }}
              />
              {!phoneVerified && (
                // <Button
                //   type="primary"
                //   onClick={sendOTP}
                //   loading={sendingOtp}
                //   disabled={showOtpField && !verifyingOtp}
                //   style={{ whiteSpace: "nowrap" }}
                // >
                //   Send OTP
                // </Button>
  <button
  onClick={sendOTP}
  disabled={showOtpField && !verifyingOtp}
  className={`${
    showOtpField && !verifyingOtp
      ? 'cursor-not-allowed opacity-50 bg-orange hover:bg-lightOrange hover:text-orange text-white'
      : 'bg-orange hover:bg-lightOrange hover:text-orange text-white'
  } font-medium px-2 text-nowrap  py-2 rounded-md shadow`}
>
  Verify Phone
</button>
              )}
              {phoneVerified && <CheckCircleOutlined style={{ color: "green", fontSize: "20px" }} />}
            </div>
          </Form.Item>

          {showOtpField && !phoneVerified && (
            <Form.Item name="otp" label="Enter OTP"  required>
              <div className="flex items-center gap-2">
                <Input placeholder="Enter the OTP sent to your phone" />
              <Button
  onClick={verifyOTP}
  loading={verifyingOtp}
  className="bg-orange hover:bg-green-700 text-white font-medium px-4 py-2 rounded-md shadow"
>
  Verify OTP
</Button>
</div>
            </Form.Item>
          )}

          {/* Only show the rest of the form if phone is verified */}
          {phoneVerified && (
            <>
              {/* Course Selection Section */}
              <div className="mb-3 text-lg font-medium border-b-[1px] border-slate-200 pb-2">
                <div>
                  <BookOutlined className="mr-3" />
                  <span>Course Information</span>
                </div>
              </div>

              <Form.Item
                name="course"
                label="Preferred Course"
                rules={[{ required: true, message: "Please select a course" }]}
              >
                <Select placeholder="Select your preferred course">
                  <Option value="Digital Marketing Course">Digital Marketing Course</Option>
                  <Option value="Web Development with WordPress & Shopify">
                    Web Development with WordPress & Shopify
                  </Option>
                  <Option value="Web Development with PHP">Web Development with PHP</Option>
                  <Option value="Web Development with Python">Web Development with Python</Option>
                  <Option value="Web Development with ReactJS & NextJS">Web Development with ReactJS & NextJS</Option>
                </Select>
              </Form.Item>

              <Form.Item name="referral" label="How Did You Hear About Us?">
                <Select placeholder="Select an option">
                  <Option value="Social Media">Social Media (Facebook, Instagram, LinkedIn, etc.)</Option>
                  <Option value="Google Search">Google Search</Option>
                  <Option value="Word of Mouth">Word of Mouth</Option>
                  <Option value="Other">Other</Option>
                </Select>
              </Form.Item>

              {/* Terms and Conditions Section */}
              <Form.Item
                name="agreement"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) =>
                      value
                        ? Promise.resolve()
                        : Promise.reject(new Error("You must agree to the terms and conditions")),
                  },
                ]}
              >
                <Checkbox>I agree to the terms and conditions of W3era Academy.</Checkbox>
              </Form.Item>

              <Form.Item>
                <button
                  type="submit"
                  className="w-full py-3 px-4 bg-orange hover:bg-lightOrange hover:text-orange text-white font-bold rounded-lg shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center"
                >
                  <DownloadOutlined style={{ marginRight: "8px" }} />
                  <span>Download Brochure</span>
                </button>
              </Form.Item>
            </>
          )}
        </Form>
      </Spin>
    </Modal>
  )
}

export default BrochureDownloadModal
